/**
 * Reset offer contracts on core-api and the Desktop-Web Bridge (TRD sections
 * 12 to 15.1). Desktop and web validate the same bridge shapes.
 *
 * The claim lives here, not on the Relay (TRD section 14). "One logical offer
 * is never displayed twice" needs a first-writer-wins write that survives
 * anything, and the Relay keeps no state, so the Desktop claims the offer row
 * in Postgres:
 *
 *   POST /api/contact-centre/reset-offers/{offerId}/claim   (member bearer)
 *       {} -> ResetOfferReportResponse (state "claimed"), or 409 ResetOfferClaimRejected
 *
 * Core-api recorded the claim itself, so the later `offered` and `decision`
 * reports need no receipt: the route checks the row's claimant and state.
 *
 * The calls trigger webhook request is not pinned here yet: its payload belongs
 * to the calls service and lands with the `reset_offers` work (INT-342).
 */
import { z } from "zod";
import { RelayResourceId, RelayTimestamp } from "./primitives.js";
export const RELAY_BRIDGE_PROTOCOL_VERSION = 1;
const bridge = {
    v: z.literal(RELAY_BRIDGE_PROTOCOL_VERSION),
    channel: z.string().min(1),
    offerId: RelayResourceId,
};
/** Desktop to embedded HealthOS Web, immediately after Accept. Retried until acknowledged. */
export const RelayResetStartMessage = z.strictObject({
    type: z.literal("healthos:reset:start"),
    ...bridge,
});
/** HealthOS Web to Desktop once the Reset experience has started; idempotent per offer. */
export const RelayResetStartedMessage = z.strictObject({
    type: z.literal("healthos:reset:started"),
    ...bridge,
});
export const ResetOfferState = z.enum([
    "pending",
    "dispatched",
    "claimed",
    "offered",
    "accepted",
    "completed",
    "abandoned",
    "declined",
    "timeout",
    "dispatch_failed",
    "delivery_failed",
]);
export const ResetOfferParams = z.strictObject({ offerId: RelayResourceId });
/**
 * Why a claim was refused. Generic on purpose: the reason helps operations and
 * never explains the business case. `already_claimed` is the normal outcome
 * for the second of two Desktops racing for one offer.
 */
export const ResetOfferClaimRejectedReason = z.enum([
    "already_claimed",
    "expired",
    "not_found",
    "not_claimable",
]);
/** The `409` body of a refused claim. The Desktop shows nothing on any reason. */
export const ResetOfferClaimRejected = z.strictObject({
    offerId: RelayResourceId,
    reason: ResetOfferClaimRejectedReason,
});
/** `POST /api/contact-centre/reset-offers/{offerId}/offered`, from Desktop after display. */
export const ResetOfferedRequest = z.strictObject({
    displayedAt: RelayTimestamp,
});
/**
 * `POST /api/contact-centre/reset-offers/{offerId}/decision`. Carries the
 * display time so a decision that overtakes its `offered` report can establish
 * both in one transaction.
 */
export const ResetDecisionRequest = z.strictObject({
    displayedAt: RelayTimestamp,
    decision: z.enum(["accept", "decline"]),
    decisionCapturedAt: RelayTimestamp,
});
/** `POST /api/contact-centre/reset-offers/{offerId}/playback-outcome`, from HealthOS Web. */
export const ResetPlaybackOutcomeRequest = z.strictObject({
    outcome: z.enum(["completed", "abandoned"]),
    occurredAt: RelayTimestamp,
    secondsWatched: z.number().nonnegative(),
    durationSeconds: z.number().positive(),
    mediaEnded: z.boolean(),
});
/** Every Reset report returns the recorded state, which a conflicting retry does not change. */
export const ResetOfferReportResponse = z.strictObject({
    offerId: RelayResourceId,
    state: ResetOfferState,
});
//# sourceMappingURL=reset.js.map